import { Box, InputBase, ListItemText, ListSubheader, MenuItem, Select, SelectProps, TextField, Typography } from '@mui/material';
import { ArrowDown } from '@vy/ui-design-system';
import { findRequired, newUUID } from '@vy/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { VyDivider } from '../../../atoms';
import { VyCheckbox } from '../../../inputs/VyCheckbox';
import { VyCheckboxSelectChips } from './VyCheckboxSelectChips';

export type SelectItem = {
  value: string;
  label: string;
};

export type VyCheckboxSelectProps = SelectProps<SelectItem[]> & {
  withSelectAll?: boolean;
  withChips?: boolean;
  selectItems: SelectItem[];
  placeholder?: string;
  onSelectionChange?: (selectedItems: SelectItem[]) => void;
  maxChipsInInputArea?: number;
};

const SEARCHABLE_THRESHOLD = 30;
const RENDER_CAP = 200;

export const VyCheckboxSelect: React.FC<VyCheckboxSelectProps> = ({
  withSelectAll = false,
  withChips = false,
  selectItems,
  onSelectionChange,
  placeholder,
  maxChipsInInputArea,
  ...props
}) => {
  const [selectedItems, setSelectedItems] = useState<SelectItem[]>([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (props.value && Array.isArray(props.value)) {
      setSelectedItems(props.value);
    }
  }, [props.value]);

  const isSearchable = selectItems.length > SEARCHABLE_THRESHOLD;
  const { visibleItems, filteredCount, isCapped } = useMemo(() => {
    const trimmed = searchText.trim().toLowerCase();
    const filtered = trimmed
      ? selectItems.filter((item) => item.label.toLowerCase().includes(trimmed))
      : selectItems;
    const capped = filtered.length > RENDER_CAP;
    return {
      visibleItems: capped ? filtered.slice(0, RENDER_CAP) : filtered,
      filteredCount: filtered.length,
      isCapped: capped,
    };
  }, [selectItems, searchText]);

  const commitSelectionChange = (newSelectedItems: SelectItem[]) => {
    setSelectedItems(newSelectedItems);
    onSelectionChange?.(newSelectedItems);
  };

  const handleItemClick = (value: string) => {
    const isItemSelected = isSelected(selectedItems, value);
    const item = findRequired(selectItems, (item) => item.value === value);

    const newSelectedItems = isItemSelected
      ? removeSelectedItem(selectedItems, value)
      : addSelectedItem(selectedItems, item);

    commitSelectionChange(newSelectedItems);
  };

  const handleAllSelect = () => {
    const areAllSelected = selectedItems.length === selectItems.length;
    const newSelectedItems = areAllSelected ? [] : [...selectItems];

    commitSelectionChange(newSelectedItems);
  };

  const handleChipDelete = (item: SelectItem) => {
    const newSelectedItems = removeSelectedItem(selectedItems, item.value);

    commitSelectionChange(newSelectedItems);
  };

  return (
    <Select
      MenuProps={{
        sx: {
          maxHeight: '300px',
        },
        autoFocus: false,
        disableAutoFocusItem: true,
        slotProps: {
          paper: {
            sx: {
              width: 280,
              maxWidth: 280,
            },
          },
          transition: {
            onExited: () => setSearchText(''),
          },
        },
      }}
      multiple
      value={selectedItems}
      IconComponent={ArrowDown}
      displayEmpty
      input={<InputBase />}
      inputProps={{
        sx: {
          backgroundColor: 'transparent',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          height: '36px !important',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          width: '170px',
        },
      }}
      renderValue={(selected) => (
        <VyCheckboxSelectChips
          onDelete={handleChipDelete}
          placeholder={placeholder}
          selectedItems={selected}
          hidden={!withChips}
          maxChipsInInputArea={maxChipsInInputArea}
        />
      )}
      sx={{
        border: 'none',
        ...props.sx,
      }}
      {...props}
    >
      {isSearchable && (
        <ListSubheader sx={{ p: 1, backgroundColor: 'common.white', lineHeight: 'normal' }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || e.key === 'ArrowDown' || e.key === 'ArrowUp') return;
              e.stopPropagation();
            }}
          />
        </ListSubheader>
      )}
      {visibleItems.map(({ value, label }) => (
        <MenuItem
          onClick={() => handleItemClick(value)}
          key={value}
          value={label}
          sx={{ minWidth: 0 }}
        >
          <VyCheckbox labelWrapperSx={{ marginRight: '4px' }} checked={isSelected(selectedItems, value)} />
          <ListItemText
            primary={label}
            sx={{ minWidth: 0, '& .MuiListItemText-primary': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            slotProps={{
              primary: { noWrap: true, title: label }
            }}
          />
        </MenuItem>
      ))}
      {isCapped && (
        <ListSubheader sx={{ p: 1, backgroundColor: 'common.white', lineHeight: 'normal' }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%"
            }}>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              Showing {visibleItems.length} of {filteredCount}. Type to narrow the list.
            </Typography>
          </Box>
        </ListSubheader>
      )}
      {isSearchable && filteredCount === 0 && (
        <ListSubheader sx={{ p: 1, backgroundColor: 'common.white', lineHeight: 'normal' }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%"
            }}>
            <Typography variant="caption" sx={{
              color: "text.secondary"
            }}>
              No matches
            </Typography>
          </Box>
        </ListSubheader>
      )}
      {withSelectAll && (
        <>
          <VyDivider />
          <MenuItem onClick={handleAllSelect} key={newUUID()} value={'Select All'}>
            <VyCheckbox
              labelWrapperSx={{ marginRight: '4px' }}
              checked={selectedItems.length === selectItems.length}
            />
            <ListItemText primary={'Select All'} />
          </MenuItem>
        </>
      )}
    </Select>
  );
};

function addSelectedItem(selectedItems: SelectItem[], item: SelectItem): SelectItem[] {
  return [...selectedItems, item];
}

function removeSelectedItem(selectedItems: SelectItem[], value: string): SelectItem[] {
  return selectedItems.filter((item) => item.value !== value);
}

function isSelected(selectedItems: SelectItem[], value: string): boolean {
  return selectedItems.some((item) => item.value === value);
}
